"use client";

import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, FileCheck2, Loader2, Play, Plus, Save, Trash2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { formatLeaseDialogCell } from "@/utils/lease-dialog-format";
import {
  computeModificationJournalEntry,
  type ModificationJournalResult,
} from "@/utils/leaseModificationJournalEntry";

interface LeaseModificationIndASProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface AssessmentLease {
  lease_name: string;
  commencement_date: string;
  lease_period: number;
  int_ror: number;
}

interface PaymentDetailRow {
  payment_classification: string;
  payment_type: string;
  payment_amount: number;
  n_payments: number;
  int_incr_text: string;
  incr_per_text: string;
  month_first_payment: number;
}

interface ModificationRow {
  id: string;
  lease_name: string;
  modification_date: string;
  scope_increased: "Yes" | "No" | "";
  lease_period_increased: "Yes" | "No" | "";
  standalone_selling_price: "Yes" | "No" | "";
  scope_reduced: "Yes" | "No" | "";
  other_reason: string;
  original_number_of_assets: number;
  revised_number_of_assets: number;
  original_commencement_date: string;
  original_lease_period: number;
  new_effective_interest_rate: number;
  total_revised_lease_period: number;
  payment_details: PaymentDetailRow[];
}

interface OutputLease {
  lease_info: Record<string, any>;
  payment_details: Array<Record<string, any>>;
  schedule: Array<Record<string, any>>;
}

const ASSESSMENT_INPUT_FILE = "Execution_LEASE_AssessmentInp.json";
const MODIFICATION_INPUT_FILE = "Execution_LEASE_ModificationInp.json";
const MODIFICATION_OUTPUT_FILE = "Execution_LEASE_Modification.json";
const ASSESSMENT_OUTPUT_FILE = "Execution_LEASE_AssessmentOut.json";
const NON_APPLICABLE_CLASSIFICATIONS = new Set(["Guaranteed Residual Value", "Purchase Price", "Penalty for Termination"]);

const defaultPaymentRow = (): PaymentDetailRow => ({
  payment_classification: "",
  payment_type: "",
  payment_amount: 0,
  n_payments: 0,
  int_incr_text: "",
  incr_per_text: "",
  month_first_payment: 0,
});

export default function LeaseModificationIndAS({ setActiveSection }: LeaseModificationIndASProps) {
  const [assessmentLeases, setAssessmentLeases] = useState<AssessmentLease[]>([]);
  const [rows, setRows] = useState<ModificationRow[]>([]);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [isRunning, setIsRunning] = useState(false);
  const [outputLeases, setOutputLeases] = useState<OutputLease[]>([]);
  const [selectedOutputLease, setSelectedOutputLease] = useState<OutputLease | null>(null);
  const [isOutputDialogOpen, setIsOutputDialogOpen] = useState(false);
  const [isJournalDialogOpen, setIsJournalDialogOpen] = useState(false);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalResult, setJournalResult] = useState<ModificationJournalResult | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const assessmentResult = await (window as any).sharePointAPI?.downloadClientJson?.(ASSESSMENT_INPUT_FILE);
        const leases = Array.isArray(assessmentResult?.data?.leases) ? assessmentResult.data.leases : [];
        const baseLeases: AssessmentLease[] = leases.map((lease: any) => ({
          lease_name: lease.lease_name || "",
          commencement_date: lease.commencement_date || "",
          lease_period: Number(lease.lease_period) || 0,
          int_ror: Number(lease.int_ror) || 0,
        }));
        setAssessmentLeases(baseLeases);

        const savedResult = await (window as any).sharePointAPI?.downloadClientJson?.(MODIFICATION_INPUT_FILE);
        const savedRows = Array.isArray(savedResult?.data?.rows) ? savedResult.data.rows : [];
        const hydratedRows: ModificationRow[] = savedRows.map((row: any, index: number) => ({
          id: `mod_${Date.now()}_${index}`,
          lease_name: row.lease_name || "",
          modification_date: toInputDate(row.modification_date || ""),
          scope_increased: row.scope_increased || "",
          lease_period_increased: row.lease_period_increased || "",
          standalone_selling_price: row.standalone_selling_price || "",
          scope_reduced: row.scope_reduced || "",
          other_reason: row.other_reason || "",
          original_number_of_assets: Number(row.original_number_of_assets) || 0,
          revised_number_of_assets: Number(row.revised_number_of_assets) || 0,
          original_commencement_date: toInputDate(row.original_commencement_date || ""),
          original_lease_period: Number(row.original_lease_period) || 0,
          new_effective_interest_rate: Number(row.new_effective_interest_rate) || 0,
          total_revised_lease_period: Number(row.total_revised_lease_period) || 0,
          payment_details: Array.isArray(row.payment_details) && row.payment_details.length > 0
            ? row.payment_details.map((p: any) => ({
                payment_classification: p.payment_classification || "",
                payment_type: p.payment_type || "",
                payment_amount: Number(p.payment_amount) || 0,
                n_payments: Number(p.n_payments) || 0,
                int_incr_text: Array.isArray(p.int_incr) ? p.int_incr.join(",") : "",
                incr_per_text: Array.isArray(p.incr_per) ? p.incr_per.join(",") : "",
                month_first_payment: Number(p.month_first_payment) || 0,
              }))
            : [defaultPaymentRow()],
        }));
        setRows(hydratedRows);
      } catch (error) {
        console.warn("[LeaseModificationIndAS] Failed to load data:", error);
      }
    };
    loadData();
    loadOutput();
  }, []);

  const loadOutput = async () => {
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.(MODIFICATION_OUTPUT_FILE);
      if (result?.success && Array.isArray(result?.data?.leases)) {
        setOutputLeases(result.data.leases);
      } else {
        setOutputLeases([]);
      }
    } catch {
      setOutputLeases([]);
    }
  };

  const leaseOptions = useMemo(
    () => assessmentLeases.map((lease) => lease.lease_name).filter(Boolean),
    [assessmentLeases],
  );

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: `mod_${Date.now()}`,
        lease_name: "",
        modification_date: "",
        scope_increased: "",
        lease_period_increased: "",
        standalone_selling_price: "",
        scope_reduced: "",
        other_reason: "",
        original_number_of_assets: 0,
        revised_number_of_assets: 0,
        original_commencement_date: "",
        original_lease_period: 0,
        new_effective_interest_rate: 0,
        total_revised_lease_period: 0,
        payment_details: [defaultPaymentRow()],
      },
    ]);
  };

  const removeRow = (id: string) => setRows((prev) => prev.filter((row) => row.id !== id));

  const updateRow = (id: string, updates: Partial<ModificationRow>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...updates } : row)));
  };

  const updateLeaseName = (id: string, leaseName: string) => {
    const base = assessmentLeases.find((lease) => lease.lease_name === leaseName);
    updateRow(id, {
      lease_name: leaseName,
      original_commencement_date: toInputDate(base?.commencement_date || ""),
      original_lease_period: Number(base?.lease_period) || 0,
      new_effective_interest_rate: Number(base?.int_ror) || 0,
      total_revised_lease_period: Number(base?.lease_period) || 0,
    });
  };

  const updatePaymentDetail = (rowId: string, paymentIndex: number, field: keyof PaymentDetailRow, value: string | number) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              payment_details: row.payment_details.map((payment, idx) =>
                idx === paymentIndex ? { ...payment, [field]: value } : payment,
              ),
            }
          : row,
      ),
    );
  };

  const addPaymentDetail = (rowId: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, payment_details: [...row.payment_details, defaultPaymentRow()] } : row)),
    );
  };

  const removePaymentDetail = (rowId: string, paymentIndex: number) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const filtered = row.payment_details.filter((_, idx) => idx !== paymentIndex);
        return { ...row, payment_details: filtered.length ? filtered : [defaultPaymentRow()] };
      }),
    );
  };

  const scenarioType = (row: ModificationRow): "first" | "second" | "third" | null => {
    const isOtherReason = row.other_reason.trim().length > 0;
    if (row.scope_reduced === "Yes") return "third";
    if (row.standalone_selling_price === "No" && row.lease_period_increased === "Yes") return "second";
    if ((row.standalone_selling_price === "No" && row.lease_period_increased === "No") || isOtherReason) return "first";
    return null;
  };

  const computedLeasePeriod = (row: ModificationRow): number => {
    if (!row.modification_date || !row.original_commencement_date) return row.original_lease_period;
    const elapsedMonths = (toJsDate(row.modification_date).getTime() - toJsDate(row.original_commencement_date).getTime()) / (1000 * 60 * 60 * 24 * 30);
    const scenario = scenarioType(row);
    if (scenario === "second" || scenario === "third") {
      return Math.max(1, Math.ceil((Number(row.total_revised_lease_period) || 0) - elapsedMonths));
    }
    return Math.max(1, Math.ceil((Number(row.original_lease_period) || 0) - elapsedMonths));
  };

  const saveData = async () => {
    if (saveStatus === "saving") return;
    setSaveStatus("saving");
    try {
      const fyYear = getFY_YEAR();
      if (!fyYear) throw new Error("FY_YEAR is not configured. Please select an engagement.");

      const payload = {
        rows: rows.map((row) => ({
          lease_name: row.lease_name,
          modification_date: row.modification_date ? fromInputDate(row.modification_date) : "",
          scope_increased: row.scope_increased,
          lease_period_increased: row.lease_period_increased,
          standalone_selling_price: row.standalone_selling_price,
          scope_reduced: row.scope_reduced,
          other_reason: row.other_reason,
          original_number_of_assets: Number(row.original_number_of_assets) || 0,
          revised_number_of_assets: Number(row.revised_number_of_assets) || 0,
          original_commencement_date: row.original_commencement_date ? fromInputDate(row.original_commencement_date) : "",
          original_lease_period: Number(row.original_lease_period) || 0,
          new_effective_interest_rate: Number(row.new_effective_interest_rate) || 0,
          total_revised_lease_period: Number(row.total_revised_lease_period) || 0,
          computed_lease_period: computedLeasePeriod(row),
          scenario: scenarioType(row),
          payment_details: row.payment_details.map((p) => {
            const nonApplicable = NON_APPLICABLE_CLASSIFICATIONS.has(p.payment_classification);
            return {
              payment_classification: p.payment_classification || "",
              payment_type: p.payment_type || "",
              payment_amount: Number(p.payment_amount) || 0,
              n_payments: nonApplicable ? 0 : Number(p.n_payments) || 0,
              int_incr: nonApplicable ? [] : parseCommaSeparatedNumbers(p.int_incr_text),
              incr_per: nonApplicable ? [] : parseCommaSeparatedNumbers(p.incr_per_text),
              month_first_payment: nonApplicable ? 0 : Number(p.month_first_payment) || 0,
            };
          }),
        })),
      };

      const result = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: btoa(JSON.stringify(payload, null, 2)),
        fileName: MODIFICATION_INPUT_FILE,
        referenceValue: "",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_LEASE_ModificationInp"],
      });

      if (!result?.success) throw new Error(result?.error || "Failed to save modification input.");
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("[LeaseModificationIndAS] Failed to save:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  };

  const runModification = async () => {
    if (isRunning || saveStatus === "saving") return;
    setIsRunning(true);
    try {
      await saveData();
      const result = await (window as any).payroll?.run?.("execute_lease_modification", {
        inputFiles: [],
        options: {
          reference_value: "Lease Modification",
        },
      });
      if (!result?.ok) {
        throw new Error(result?.error || "Failed to run Lease Modification");
      }
      await loadOutput();
    } catch (error) {
      alert(`Failed to run Lease Modification: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsRunning(false);
    }
  };

  const openOutput = (leaseName: string) => {
    const matched = outputLeases.find(
      (item) => String(item?.lease_info?.["Name of Lease"] || "").trim() === String(leaseName || "").trim(),
    );
    if (!matched) {
      alert("Output not found for this lease. Please click Run first.");
      return;
    }
    setSelectedOutputLease(matched);
    setIsOutputDialogOpen(true);
  };

  const formatJeAmount = (n: number) => formatLeaseDialogCell(n, "Journal");

  const openJournalEntry = async (row: ModificationRow) => {
    if (!String(row.lease_name || "").trim()) {
      alert("Select a lease first.");
      return;
    }
    setIsJournalDialogOpen(true);
    setJournalLoading(true);
    setJournalResult(null);
    try {
      const download = async (file: string) => {
        const res = await (window as any).sharePointAPI?.downloadClientJson?.(file);
        return res?.success && res?.data != null ? res.data : null;
      };
      const [assessment, modification] = await Promise.all([
        download(ASSESSMENT_OUTPUT_FILE),
        download(MODIFICATION_OUTPUT_FILE),
      ]);
      if (!assessment) {
        setJournalResult({
          ok: false,
          message: `Could not load ${ASSESSMENT_OUTPUT_FILE}. Run Lease Testing first.`,
        });
        return;
      }
      if (!modification) {
        setJournalResult({
          ok: false,
          message: `Could not load ${MODIFICATION_OUTPUT_FILE}. Run Lease Modification first.`,
        });
        return;
      }
      setJournalResult(
        computeModificationJournalEntry(
          row.lease_name,
          {
            scope_reduced: row.scope_reduced,
            standalone_selling_price: row.standalone_selling_price,
            lease_period_increased: row.lease_period_increased,
            other_reason: row.other_reason,
            original_number_of_assets: row.original_number_of_assets,
            revised_number_of_assets: row.revised_number_of_assets,
          },
          assessment,
          modification,
        ),
      );
    } catch (e) {
      setJournalResult({
        ok: false,
        message: e instanceof Error ? e.message : "Failed to load journal data.",
      });
    } finally {
      setJournalLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Lease Modification</h2>
          <p className="text-gray-400">Capture lease modification conditions and fresh payment inputs</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={runModification} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={isRunning || saveStatus === "saving"}>
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run
              </>
            )}
          </Button>
          <Button onClick={saveData} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saveStatus === "saving"}>
            {saveStatus === "saving" ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
          {saveStatus === "saved" && <span className="text-sm text-green-400">Saved!</span>}
          <Button variant="outline" onClick={() => setActiveSection?.("ll-substantive")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <FileCheck2 className="h-5 w-5 text-blue-500" />
              Lease Modification Table
            </div>
            <Button onClick={addRow} size="sm" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {rows.length === 0 ? (
            <div className="text-center py-8 text-white/60">No modification rows added yet.</div>
          ) : (
            rows.map((row, index) => {
              const scenario = scenarioType(row);
              const disableLeasePeriodIncreased = row.scope_increased !== "Yes";
              const disableStandalone = row.scope_increased !== "Yes";
              const disableScopeReduced = row.scope_increased === "Yes";
              const disableOtherReason = row.scope_increased === "Yes" || row.scope_reduced === "Yes";
              return (
                <div key={row.id} className="border border-white/10 rounded-lg p-4 bg-white/5 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-white font-medium">Lease Modification #{index + 1}</h4>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white"
                        onClick={() => void openJournalEntry(row)}
                        data-allow-archival-edit={true}
                      >
                        Journal Entry
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white"
                        onClick={() => openOutput(row.lease_name)}
                        data-allow-archival-edit={true}
                      >
                        Open
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => removeRow(row.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Lease Name</Label>
                      <Select value={row.lease_name} onValueChange={(value) => updateLeaseName(row.id, value)}>
                        <SelectTrigger><SelectValue placeholder="Select lease" /></SelectTrigger>
                        <SelectContent>
                          {leaseOptions.map((name) => (
                            <SelectItem key={name} value={name}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Lease modification date</Label>
                      <Input type="date" value={row.modification_date} onChange={(e) => updateRow(row.id, { modification_date: e.target.value })} className="border-white/10 bg-black/40 text-white" />
                    </div>
                    <YesNoField label="Has scope of lease increased?" value={row.scope_increased} onChange={(value) => updateRow(row.id, { scope_increased: value, lease_period_increased: value === "Yes" ? row.lease_period_increased : "", standalone_selling_price: value === "Yes" ? row.standalone_selling_price : "", scope_reduced: value === "Yes" ? "No" : row.scope_reduced, other_reason: value === "Yes" ? "" : row.other_reason })} />
                    <YesNoField label="Has the lease period increased" value={row.lease_period_increased} onChange={(value) => updateRow(row.id, { lease_period_increased: value })} disabled={disableLeasePeriodIncreased} />
                    <YesNoField label="Is new component charged at standalone selling price?" value={row.standalone_selling_price} onChange={(value) => updateRow(row.id, { standalone_selling_price: value })} disabled={disableStandalone} />
                    <YesNoField label="Has scope reduced?" value={row.scope_reduced} onChange={(value) => updateRow(row.id, { scope_reduced: value, other_reason: value === "Yes" ? "" : row.other_reason })} disabled={disableScopeReduced} />
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Any other reason?</Label>
                      <Input value={row.other_reason} onChange={(e) => updateRow(row.id, { other_reason: e.target.value })} disabled={disableOtherReason} className={`border-white/10 bg-black/40 text-white ${disableOtherReason ? "opacity-50 cursor-not-allowed" : ""}`} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Original Number of Assets</Label>
                      <Input
                        type="number"
                        min={0}
                        value={row.original_number_of_assets}
                        onChange={(e) => updateRow(row.id, { original_number_of_assets: parseInt(e.target.value, 10) || 0 })}
                        className="border-white/10 bg-black/40 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Revised Number of Assets</Label>
                      <Input
                        type="number"
                        min={0}
                        value={row.revised_number_of_assets}
                        onChange={(e) => updateRow(row.id, { revised_number_of_assets: parseInt(e.target.value, 10) || 0 })}
                        className="border-white/10 bg-black/40 text-white"
                      />
                    </div>
                  </div>

                  {scenario && (
                    <div className="border border-white/10 rounded-lg p-3 bg-black/20 space-y-3">
                      <h5 className="text-white font-medium">
                        {row.lease_name || "Lease"} - {scenario === "first" ? "Modification Case 1" : scenario === "second" ? "Modification Case 2" : "Modification Case 3"}
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {(scenario === "first" || scenario === "second") && (
                          <div className="space-y-2">
                            <Label className="text-white text-sm">New Effective Interest Rate</Label>
                            <Input type="number" value={row.new_effective_interest_rate} onChange={(e) => updateRow(row.id, { new_effective_interest_rate: parseFloat(e.target.value) || 0 })} className="border-white/10 bg-black/40 text-white" />
                          </div>
                        )}
                        {(scenario === "second" || scenario === "third") && (
                          <div className="space-y-2">
                            <Label className="text-white text-sm">New Total Revised Lease Period</Label>
                            <Input type="number" value={row.total_revised_lease_period} onChange={(e) => updateRow(row.id, { total_revised_lease_period: parseInt(e.target.value) || 0 })} className="border-white/10 bg-black/40 text-white" />
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label className="text-white text-sm">Computed Lease Period</Label>
                          <Input type="number" value={computedLeasePeriod(row)} disabled className="border-white/10 bg-black/40 text-white opacity-70" />
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <h6 className="text-white/90 font-medium">Fresh Payment Input Table</h6>
                        <Button onClick={() => addPaymentDetail(row.id)} size="sm" className="bg-green-600 hover:bg-green-700">
                          <Plus className="h-4 w-4 mr-2" />
                          Add Row
                        </Button>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-white/10">
                          <thead>
                            <tr className="bg-black/60">
                              <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Classification</th>
                              <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Type</th>
                              <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Amount (per month/year)</th>
                              <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">No. of months</th>
                              <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Interval of Increment</th>
                              <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Increment %</th>
                              <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Month of First Payment</th>
                              <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {row.payment_details.map((payment, paymentIndex) => {
                              const nonApplicable = NON_APPLICABLE_CLASSIFICATIONS.has(payment.payment_classification);
                              return (
                                <tr key={`${row.id}_${paymentIndex}`} className="hover:bg-white/5">
                                  <td className="border border-white/10 px-2 py-2">
                                    <Select value={payment.payment_classification} onValueChange={(value) => updatePaymentDetail(row.id, paymentIndex, "payment_classification", value)}>
                                      <SelectTrigger><SelectValue placeholder="Select classification" /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Fixed Payment">Fixed Payment</SelectItem>
                                        <SelectItem value="Variable Payment">Variable Payment</SelectItem>
                                        <SelectItem value="Guaranteed Residual Value">Guaranteed Residual Value</SelectItem>
                                        <SelectItem value="Purchase Price">Purchase Price</SelectItem>
                                        <SelectItem value="Penalty for Termination">Penalty for Termination</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="border border-white/10 px-2 py-2">
                                    <Input value={payment.payment_type} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "payment_type", e.target.value)} className="border-white/10 bg-black/40 text-white" />
                                  </td>
                                  <td className="border border-white/10 px-2 py-2">
                                    <Input type="number" value={payment.payment_amount} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "payment_amount", parseFloat(e.target.value) || 0)} className="border-white/10 bg-black/40 text-white" />
                                  </td>
                                  <td className="border border-white/10 px-2 py-2">
                                    <Input type="number" value={payment.n_payments} disabled={nonApplicable} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "n_payments", parseInt(e.target.value) || 0)} className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                                  </td>
                                  <td className="border border-white/10 px-2 py-2">
                                    <Input value={payment.int_incr_text} disabled={nonApplicable} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "int_incr_text", e.target.value)} className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                                  </td>
                                  <td className="border border-white/10 px-2 py-2">
                                    <Input value={payment.incr_per_text} disabled={nonApplicable} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "incr_per_text", e.target.value)} className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                                  </td>
                                  <td className="border border-white/10 px-2 py-2">
                                    <Input type="number" value={payment.month_first_payment} disabled={nonApplicable} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "month_first_payment", parseInt(e.target.value) || 0)} className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                                  </td>
                                  <td className="border border-white/10 px-2 py-2">
                                    <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => removePaymentDetail(row.id, paymentIndex)}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <Dialog open={isJournalDialogOpen} onOpenChange={setIsJournalDialogOpen}>
        <DialogContent className="max-w-3xl w-[95vw] bg-black border-white/20 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lease Modification (journal entry)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            {journalLoading ? (
              <div className="flex items-center gap-2 text-white/80">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading assessment and modification outputs…
              </div>
            ) : !journalResult ? (
              <div className="text-white/60">No data.</div>
            ) : !journalResult.ok ? (
              <div className="text-amber-200/90">{journalResult.message}</div>
            ) : journalResult.journalCase === 1 ? (
              <>
                <p className="text-white/70">
                  Case 1 — Reduction in scope. Liability difference (same basis as reassessment): event{" "}
                  <span className="text-white">{journalResult.eventMonth}</span> vs assessment closing{" "}
                  <span className="text-white">{journalResult.priorMonth}</span>. ROU credit uses assessment ROU for{" "}
                  <span className="text-white">{journalResult.rouNextMonthLabel}</span> × (original − revised) / original assets.
                </p>
                <div className="overflow-x-auto rounded border border-white/10">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-black/60 border-b border-white/10">
                        <th className="text-left px-3 py-2 text-white font-medium">Particulars</th>
                        <th className="w-12 px-2 py-2" />
                        <th className="text-right px-3 py-2 text-white font-medium min-w-[7rem]">Amount</th>
                        <th className="text-right px-3 py-2 text-white font-medium min-w-[7rem]">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="text-white">
                      <tr>
                        <td className="px-3 py-2">Lease Liability</td>
                        <td className="px-2 py-2 text-white/90">Dr</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatJeAmount(journalResult.drLeaseLiability)}</td>
                        <td className="px-3 py-2 text-right text-white/30">—</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2">Profit and Loss</td>
                        <td className="px-2 py-2 text-white/90">Dr</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatJeAmount(journalResult.drProfitAndLoss)}</td>
                        <td className="px-3 py-2 text-right text-white/30">—</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 pl-6 text-white/90">To Right of Use Asset</td>
                        <td className="px-2 py-2" />
                        <td className="px-3 py-2 text-right text-white/30">—</td>
                        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                          {formatJeAmount(journalResult.crRightOfUseAsset)}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 pl-6 text-white/90">To Profit and Loss</td>
                        <td className="px-2 py-2" />
                        <td className="px-3 py-2 text-right text-white/30">—</td>
                        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                          {formatJeAmount(journalResult.crProfitAndLoss)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <>
                <p className="text-white/70">
                  Case {journalResult.journalCase} — Modification ({journalResult.journalCase === 3 ? "period increased" : "other / period not increased"}
                  ). Event <span className="text-white">{journalResult.eventMonth}</span> opening{" "}
                  {formatJeAmount(journalResult.openingEvent)} vs assessment closing{" "}
                  <span className="text-white">{journalResult.priorMonth}</span> {formatJeAmount(journalResult.closingPriorAssessment)}.
                </p>
                {journalResult.amount === 0 ? (
                  <p className="text-white/80">No journal adjustment required (difference rounds to zero).</p>
                ) : (
                  <div className="overflow-x-auto rounded border border-white/10">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-black/60 border-b border-white/10">
                          <th className="text-left px-3 py-2 text-white font-medium">Particulars</th>
                          <th className="w-12 px-2 py-2" />
                          <th className="text-right px-3 py-2 text-white font-medium">Amount</th>
                          <th className="text-right px-3 py-2 text-white font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="text-white">
                          <td className="px-3 py-2">
                            {journalResult.delta > 0 ? "Right of Use Asset" : "Lease Liability"}
                          </td>
                          <td className="px-2 py-2 text-white/90">Dr</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatJeAmount(journalResult.amount)}</td>
                          <td className="px-3 py-2 text-right text-white/30">—</td>
                        </tr>
                        <tr className="text-white">
                          <td className="px-3 py-2 pl-6 text-white/90">
                            To {journalResult.delta > 0 ? "Lease Liability" : "Right of Use Asset"}
                          </td>
                          <td className="px-2 py-2" />
                          <td className="px-3 py-2 text-right text-white/30">—</td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                            {formatJeAmount(journalResult.amount)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isOutputDialogOpen} onOpenChange={setIsOutputDialogOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] bg-black border-white/20 text-white overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Lease Modification Output - {selectedOutputLease?.lease_info?.["Name of Lease"] || ""}
            </DialogTitle>
          </DialogHeader>
          <div className="h-full overflow-auto space-y-4 pr-1">
            {!selectedOutputLease ? (
              <div className="text-white/60">No lease output selected.</div>
            ) : (
              <>
                <OutputTable rows={[selectedOutputLease.lease_info || {}]} />
                <OutputTable rows={selectedOutputLease.payment_details || []} />
                <OutputTable rows={selectedOutputLease.schedule || []} />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OutputTable({ rows }: { rows: Array<Record<string, any>> }) {
  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  if (headers.length === 0) return <div className="text-white/60">No data available.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse border border-white/10">
        <thead>
          <tr className="bg-black/60">
            {headers.map((header) => (
              <th key={header} className="border border-white/10 px-3 py-2 text-left text-white text-sm whitespace-nowrap">
                {header || " "}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-white/5">
              {headers.map((header) => (
                <td key={`${idx}-${header}`} className="border border-white/10 px-3 py-2 text-white text-sm whitespace-nowrap">
                  {formatLeaseDialogCell(row[header], header)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function YesNoField({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: "Yes" | "No" | "";
  onChange: (value: "Yes" | "No") => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-white text-sm">{label}</Label>
      <Select value={value} onValueChange={(val) => onChange(val as "Yes" | "No")} disabled={disabled}>
        <SelectTrigger className={disabled ? "opacity-50 cursor-not-allowed" : ""}>
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Yes">Yes</SelectItem>
          <SelectItem value="No">No</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function parseCommaSeparatedNumbers(value: string): number[] {
  if (!value || !value.trim()) return [];
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v) => Number(v))
    .filter((v) => !Number.isNaN(v));
}

function toInputDate(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }
  const parts = value.includes("/") ? value.split("/") : [];
  if (parts.length === 3) {
    const month = parts[0].padStart(2, "0");
    const day = parts[1].padStart(2, "0");
    const year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
    return `${year}-${month}-${day}`;
  }
  return "";
}

function fromInputDate(inputDate: string): string {
  if (!inputDate) return "";
  const [year, month, day] = inputDate.split("-");
  return `${month}/${day}/${year}`;
}

function toJsDate(inputDate: string): Date {
  return new Date(`${inputDate}T00:00:00`);
}
